import { useMemo } from 'react';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { dateFormatter } from '../../../../../../../../../utils/grid';
import { nestedFieldValueGetter } from '../../../../../../../../shared/DataGrid/utils/valueGetters/nestedFieldValueGetter';

import { GridColDef } from '@mui/x-data-grid-pro';

import BooleanCell from '../../../../../../../../shared/Table/TableCell/DataGrid/BooleanCell';

const useColumnDefinitions = (): GridColDef[] => {
  const { t } = useTranslation('common');

  return useMemo(() => {
    return columnDefinitions(t);
  }, [t]);
};

const columnDefinitions = (t: TFunction): GridColDef[] => [
  {
    field: 'code',
    flex: 1,
    headerName: t('costCenterDetail.code')
  },
  {
    field: 'name',
    flex: 1,
    headerName: t('costCenterDetail.name')
  },
  {
    field: 'nameShort',
    flex: 1,
    headerName: t('costCenterDetail.shortName')
  },
  {
    field: 'codeExt',
    flex: 1,
    headerName: t('costCenterDetail.exportCode')
  },
  {
    field: 'codeExtSecondary',
    flex: 1,
    headerName: t('costCenterDetail.exportCode2')
  },
  {
    field: 'codeImp',
    flex: 1,
    headerName: t('costCenterDetail.importCode')
  },
  {
    field: 'codeImpSecondary',
    flex: 1,
    headerName: t('costCenterDetail.importCode2')
  },
  {
    field: 'benefitStartDate',
    flex: 1,
    headerName: t('costCenterDetail.importStartDate')
  },
  {
    field: 'financialBrNameShort',
    flex: 1,
    headerName: t('costCenterDetail.financialCostCenter')
  },
  {
    field: 'parentBrNameShort',
    flex: 1,
    headerName: t('costCenterDetail.parentCostCenter')
  },
  {
    field: 'flags.BUH_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.noBUH'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: nestedFieldValueGetter
  },
  {
    field: 'flags.VAT_DEDUCTIBLE',
    flex: 1,
    headerName: t('costCenterDetail.includeInExport'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: nestedFieldValueGetter
  },
  {
    field: 'flags.PLANNING_EXPORT',
    flex: 1,
    headerName: t('costCenterDetail.VATDeductible'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: nestedFieldValueGetter
  },
  {
    field: 'flags.INVOICE_DEBIT_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.excludeBorrowFromInvoice'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: nestedFieldValueGetter
  },
  {
    field: 'flags.INVOICE_CREDIT_BLOCKED',
    flex: 1,
    headerName: t('costCenterDetail.excludeLendFromInvoice'),
    type: 'boolean',
    renderCell: BooleanCell,
    valueGetter: nestedFieldValueGetter
  },
  {
    field: 'valid.from',
    flex: 1,
    headerName: t('validFrom'),
    valueGetter: nestedFieldValueGetter,
    valueFormatter: dateFormatter
  },
  {
    field: 'valid.to',
    flex: 1,
    headerName: t('validTo'),
    type: 'date',
    valueGetter: nestedFieldValueGetter,
    valueFormatter: dateFormatter
  },
  {
    field: 'employerName',
    flex: 1,
    headerName: t('costCenterDetail.employer')
  }
];

export default useColumnDefinitions;
